import { useCallback, useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Repeat, X } from 'lucide-react';
import { puedeUsarCanal } from '@shared/permisos';
import type { EntregaTurnoVista } from '@shared/types';
import { useAjustesPermisos } from '@renderer/store/useAjustesPermisos';
import { useSessionStore } from '@renderer/store/useSessionStore';

/**
 * Sprint 172 (D-1) — **El globo del cambio de turno**, para la caja y la dueña.
 *
 * José: *"debe enviar un mensaje tipo globo al cajero o dueños (en línea) en el momento que se dé el
 * cambio"*. Copia el patrón de `AvisosDomicilio` (sondeo, sin repetir lo ya dicho, encima de cualquier
 * pantalla) con dos diferencias deliberadas:
 *  - **No se va solo.** Un cambio de turno es algo que quien manda tiene que ver; se quita al tocarlo.
 *  - **Lo de los últimos 30 minutos sí se anuncia al entrar.** Si la cajera abrió la sesión después del
 *    cambio, igual se entera. Lo ya visto en este navegador no se repite (se guarda en la sesión del
 *    navegador, no en la base).
 * No lleva dinero: quién, a quién, cuántas mesas y la hora. El detalle está en la bitácora, con PIN.
 */
const VISTOS = 'rpos.entregasTurnoVistas';

function leerVistos(): Set<number> {
  try {
    const v = JSON.parse(sessionStorage.getItem(VISTOS) ?? '[]');
    return new Set(Array.isArray(v) ? v.filter((x) => typeof x === 'number') : []);
  } catch {
    return new Set();
  }
}

export function AvisoEntregaTurno() {
  const navigate = useNavigate();
  const location = useLocation();
  const sesion = useSessionStore((s) => s.sesion);
  const ajustes = useAjustesPermisos((s) => s.ajustes);
  const puedeVer = puedeUsarCanal(sesion?.rol, 'pedidos:entregas-recientes', ajustes);
  const [visibles, setVisibles] = useState<EntregaTurnoVista[]>([]);
  const vistos = useRef<Set<number>>(leerVistos());

  const marcarVisto = (id: number) => {
    vistos.current.add(id);
    try {
      sessionStorage.setItem(VISTOS, JSON.stringify([...vistos.current].slice(-200)));
    } catch {
      /* sin almacenamiento: a lo más se repite un globo */
    }
    setVisibles((v) => v.filter((x) => x.id !== id));
  };

  const revisar = useCallback(async () => {
    const r = await window.api.pedidos.entregasRecientes();
    if (!r.ok) return;
    const nuevas = r.data.filter((e) => !vistos.current.has(e.id));
    if (nuevas.length === 0) return;
    setVisibles((v) => {
      const ya = new Set(v.map((x) => x.id));
      return [...v, ...nuevas.filter((e) => !ya.has(e.id)).reverse()];
    });
  }, []);

  useEffect(() => {
    if (!puedeVer) return;
    void revisar();
    const t = setInterval(() => void revisar(), 15000);
    return () => clearInterval(t);
  }, [puedeVer, revisar]);

  const enLaBitacora = location.pathname.startsWith('/cambios-de-turno');
  if (!puedeVer || enLaBitacora || visibles.length === 0) return null;

  return (
    <div className="pointer-events-none fixed right-4 top-20 z-[70] flex w-[22rem] max-w-[calc(100vw-2rem)] flex-col gap-2" role="status" aria-live="polite">
      {visibles.slice(-3).map((e) => {
        const hora = new Date(e.fecha).toLocaleTimeString('es-MX', { hour: '2-digit', minute: '2-digit' });
        const mesas = e.cantidad === 1 ? '1 mesa' : `${e.cantidad} mesas`;
        const titulo =
          e.modo === 'reasignacion'
            ? `${e.autorizoNombre} le pasó ${mesas} a ${e.aNombre}`
            : `${e.deNombre} le entregó ${mesas} a ${e.aNombre}`;
        // Sprint 172 (ux-master #2/#3) — Dos botones hermanos: uno abre la bitácora, el otro cierra. La X
        // mide 44 px y se alcanza con Tab; antes era un `span` dentro del botón (HTML inválido, sin teclado).
        return (
          <div
            key={e.id}
            className="pointer-events-auto flex items-stretch rounded-xl border-2 border-violet-300 bg-violet-50 text-violet-950 shadow-lg"
          >
            <button
              type="button"
              onClick={() => {
                marcarVisto(e.id);
                navigate('/cambios-de-turno');
              }}
              className="flex min-w-0 flex-1 items-start gap-3 rounded-l-xl p-3 text-left transition hover:brightness-95"
            >
              <Repeat className="mt-0.5 h-5 w-5 shrink-0" aria-hidden />
              <span className="min-w-0 flex-1">
                <span className="block text-sm font-bold">Cambio de turno · {hora}</span>
                <span className="block text-sm">{titulo}</span>
                <span className="mt-1 block text-sm font-semibold underline">Toca para ver la bitácora</span>
              </span>
            </button>
            <button
              type="button"
              onClick={() => marcarVisto(e.id)}
              aria-label="Cerrar aviso"
              className="flex min-h-[44px] min-w-[44px] shrink-0 items-center justify-center rounded-r-xl border-l border-violet-200 hover:bg-violet-100"
            >
              <X className="h-5 w-5" aria-hidden />
            </button>
          </div>
        );
      })}
    </div>
  );
}
